import { clamp } from './geometry';

const SCROLLABLE = /(auto|scroll|overlay)/;

function isScrollable(el: Element): boolean {
  const style = window.getComputedStyle(el);
  return (
    SCROLLABLE.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1
  );
}

/** Nearest ancestor that actually scrolls, or null when the window is the scroller. */
export function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Park the target where the card still has room: centred on roomy viewports, in the
 * upper third on short/mobile ones so the card can sit below it instead of over it.
 */
export function scrollOnboardingTargetIntoView(
  el: HTMLElement,
  compact: boolean,
  behavior: ScrollBehavior = 'smooth'
): void {
  // Reveal horizontally clipped columns and vertically clipped nested panels first.
  // A horizontal table wrapper must not prevent its outer page from scrolling.
  el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior });
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const scroller = findScrollParent(el);
  // Tall tables (Cost Price dish list) should pin to the top so the hole does not
  // cover KPIs above them. Short targets stay centred so the card has room around them.
  const tall = rect.height > viewportHeight * 0.55;
  const anchorRatio = compact ? 0.32 : 0.45;

  if (scroller) {
    const box = scroller.getBoundingClientRect();
    const desiredTop = tall
      ? box.top + 8
      : box.top + box.height * anchorRatio - rect.height / 2;
    const delta = rect.top - desiredTop;
    if (Math.abs(delta) < 8) return;
    scroller.scrollTo({
      top: clamp(scroller.scrollTop + delta, 0, scroller.scrollHeight - scroller.clientHeight),
      behavior,
    });
    return;
  }

  const desiredTop = tall ? 16 : viewportHeight * anchorRatio - rect.height / 2;
  const delta = rect.top - desiredTop;
  if (Math.abs(delta) < 8) return;
  window.scrollBy({ top: delta, behavior });
}
